package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.DocumentDownload
import com.dotdrives.model.GiverExperienceStats
import com.dotdrives.model.GiverFile
import com.dotdrives.model.GiverGift
import com.dotdrives.model.GiverStatement
import com.dotdrives.model.MAX_GIVER_FILE_BYTES
import com.dotdrives.model.giverFileMimeTypes
import com.dotdrives.repository.GiverExperienceRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

data class GiverExperienceUiState(
    val giverId: String? = null,
    val loading: Boolean = false,
    val stats: GiverExperienceStats? = null,
    val gifts: List<GiverGift> = emptyList(),
    val statements: List<GiverStatement> = emptyList(),
    val files: List<GiverFile> = emptyList(),
    val uploading: Boolean = false,
    val document: DocumentDownload? = null,
    val documentId: String? = null,
    val documentBusy: Boolean = false,
    val downloadUrl: String? = null,
    val message: String? = null,
    val error: String? = null,
)

class GiverExperienceController(
    private val repo: GiverExperienceRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val canFinance: Boolean,
    private val canWrite: Boolean,
) : DotController<GiverExperienceUiState>(GiverExperienceUiState(), scope) {
    private var generation = 0
    private var loadJob: Job? = null
    private var documentJob: Job? = null
    private var mutationJob: Job? = null

    fun close() {
        generation++
        loadJob?.cancel()
        documentJob?.cancel()
        mutationJob?.cancel()
        update { GiverExperienceUiState() }
    }

    fun open(giverId: String) {
        close()
        val request = generation
        update { GiverExperienceUiState(giverId = giverId, loading = true) }
        loadJob = scope.launch {
            try {
                val stats = repo.stats(orgId, giverId).getOrThrow()
                val files = repo.files(orgId, giverId).getOrThrow()
                val gifts = if (canFinance) repo.gifts(orgId, giverId).getOrThrow() else emptyList()
                val statements = if (canFinance) repo.statements(orgId, giverId).getOrThrow() else emptyList()
                if (request == generation) {
                    update {
                        it.copy(
                            loading = false,
                            stats = if (canFinance) stats else stats.copy(finance = null),
                            gifts = gifts,
                            statements = statements,
                            files = files,
                        )
                    }
                }
            } catch (error: Throwable) {
                failure(error, request)
            }
        }
    }

    fun upload(name: String, mime: String, bytes: ByteArray) {
        val giver = state.value.giverId ?: return
        if (!canWrite || state.value.uploading) return
        if (mime !in giverFileMimeTypes || bytes.isEmpty() || bytes.size > MAX_GIVER_FILE_BYTES) {
            update { it.copy(error = "Choose a PDF, image, plain text, or CSV file up to 20 MiB") }
            return
        }
        val request = generation
        update { it.copy(uploading = true, error = null) }
        mutationJob = scope.launch {
            try {
                repo.upload(orgId, giver, name, mime, bytes).getOrThrow()
                val files = repo.files(orgId, giver).getOrThrow()
                if (request == generation) {
                    update { it.copy(uploading = false, files = files, message = "File uploaded") }
                }
            } catch (error: Throwable) {
                failure(error, request)
            }
        }
    }

    fun downloadFile(file: GiverFile) {
        val giver = state.value.giverId ?: return
        val request = generation
        documentJob?.cancel()
        documentJob = scope.launch {
            try {
                val url = repo.downloadFile(orgId, giver, file).getOrThrow()
                if (request == generation) update { it.copy(downloadUrl = url, error = null) }
            } catch (error: Throwable) {
                failure(error, request)
            }
        }
    }

    fun clearDownload() {
        update { it.copy(downloadUrl = null) }
    }

    fun downloadStatement(id: String) {
        poll(id, false)
    }

    fun retryStatement(id: String) {
        poll(id, true)
    }

    private fun poll(id: String, retry: Boolean) {
        if (!canFinance || state.value.giverId == null) return
        documentJob?.cancel()
        val request = generation
        update {
            it.copy(
                documentId = id,
                document = null,
                documentBusy = true,
                error = null,
                downloadUrl = null,
                message = null,
            )
        }
        documentJob = scope.launch {
            try {
                if (retry) repo.retryStatement(id).getOrThrow()
                repeat(12) { attempt ->
                    val result = repo.downloadStatement(id).getOrThrow()
                    if (request != generation) return@launch
                    update { it.copy(document = result, downloadUrl = result.url) }
                    if (result.status in setOf("ready", "failed")) {
                        update { it.copy(documentBusy = false) }
                        return@launch
                    }
                    if (attempt < 11) delay(5000)
                }
                if (request == generation) {
                    update {
                        it.copy(documentBusy = false, message = "Your document is still queued. Check again shortly.")
                    }
                }
            } catch (error: Throwable) {
                failure(error, request)
            }
        }
    }

    fun requestStatement(year: Int) {
        val giver = state.value.giverId ?: return
        if (!canFinance || state.value.documentBusy) return
        val request = generation
        val existing = state.value.statements.firstOrNull { it.year == year }
        if (existing?.emailedAt != null) {
            update {
                it.copy(message = "This statement email was already accepted by the provider. Its issued snapshot is unchanged.")
            }
            return
        }
        update { it.copy(documentBusy = true, error = null) }
        mutationJob = scope.launch {
            try {
                repo.requestStatement(orgId, giver, year).getOrThrow()
                val rows = repo.statements(orgId, giver).getOrThrow()
                if (request == generation) {
                    update {
                        it.copy(
                            statements = rows,
                            documentBusy = false,
                            message = "Statement email requested. Existing requests and issued totals are preserved; provider acceptance is shown when recorded.",
                        )
                    }
                }
            } catch (error: Throwable) {
                failure(error, request)
            }
        }
    }

    private fun failure(error: Throwable, request: Int) {
        if (error is CancellationException) throw error
        if (request == generation) {
            update {
                it.copy(
                    loading = false,
                    uploading = false,
                    documentBusy = false,
                    error = error.message ?: "Unable to load. Please try again.",
                )
            }
        }
    }
}
